import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import toast from "react-hot-toast";
import { useEffect, useRef } from "react";
import {
  isModuleOrCapabilityEnabled,
  type ModuleCode,
} from "@medicorepay/shared";

interface ModuleProtectedRouteProps {
  requiredModule: ModuleCode;
  children?: React.ReactNode;
}

export function ModuleProtectedRoute({
  requiredModule,
  children,
}: ModuleProtectedRouteProps) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const hasShownToast = useRef(false);

  const enabledModules = user?.enabledModules;

  // If enabledModules is undefined (not loaded yet), allow access temporarily
  if (!enabledModules) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-gray-500 text-sm">Loading access…</div>
      </div>
    );
  }

  const hasAccess = isModuleOrCapabilityEnabled(requiredModule, enabledModules);

  useEffect(() => {
    if (!hasAccess && !hasShownToast.current) {
      toast.error(
        `Access denied: ${requiredModule} module is not included in your plan`,
        {
          duration: 4000,
          id: "module-access-denied", // Prevent duplicate toasts
        },
      );
      hasShownToast.current = true;
    }
  }, [hasAccess, requiredModule]);

  if (!hasAccess) {
    // Redirect to dashboard with a message
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
